import React, { useState } from "react";
import "./App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Compact widget version of the calculator
function Widget({ embedded = false }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    industry: "",
    total_employees: 50,
    employees_left: 5,
    average_salary: 30000,
    recruitment_weeks: 6,
    sick_days: 20,
    mental_health_percentage: 30,
    has_presenteeism: false
  });

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailData, setEmailData] = useState({
    email: "",
    first_name: "",
    poster_pack_requested: false,
    company_name: "",
    address: ""
  });
  const [emailSent, setEmailSent] = useState(false);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateResults = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-culture-cost`, formData);
      setResults(response.data);
      setCurrentStep(2);
    } catch (err) {
      console.error('Calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!emailData.email || !emailData.first_name) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/send-results-email`, {
        email: emailData.email,
        first_name: emailData.first_name,
        results: results,
        poster_pack_requested: emailData.poster_pack_requested,
        company_name: emailData.company_name,
        address: emailData.address
      });
      setEmailSent(true);
      setShowEmailForm(false);
    } catch (err) {
      console.error('Email error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const widgetStyles = embedded ? {
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  } : {};

  // Step 0: Initial form
  if (currentStep === 0) {
    return (
      <div style={widgetStyles} className={`${embedded ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8'}`}>
        <div className={`bg-white rounded-xl shadow-lg p-8 ${embedded ? '' : 'max-w-2xl mx-auto'}`}>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              Workplace Wellbeing Calculator
            </h2>
            <p className="text-slate-600">
              Discover the financial impact of workplace wellbeing challenges in your organisation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Quick form inputs */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Industry
              </label>
              <select
                value={formData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select industry</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="construction">Construction</option>
                <option value="transport">Transport & Logistics</option>
                <option value="engineering">Engineering</option>
                <option value="utilities">Utilities</option>
                <option value="wind">Wind Energy</option>
                <option value="energy">Energy Sector</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Total Employees: {formData.total_employees}
              </label>
              <input
                type="range"
                min="1"
                max="500"
                value={formData.total_employees}
                onChange={(e) => handleInputChange('total_employees', parseInt(e.target.value))}
                className="w-full slider"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Employees who left (last 12 months): {formData.employees_left}
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.employees_left}
                onChange={(e) => handleInputChange('employees_left', parseInt(e.target.value))}
                className="w-full slider"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Average salary: {formatCurrency(formData.average_salary)}
              </label>
              <input
                type="range"
                min="15000"
                max="80000"
                step="1000"
                value={formData.average_salary}
                onChange={(e) => handleInputChange('average_salary', parseInt(e.target.value))}
                className="w-full slider"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Recruitment time (weeks): {formData.recruitment_weeks}
              </label>
              <input
                type="range"
                min="1"
                max="12"
                value={formData.recruitment_weeks}
                onChange={(e) => handleInputChange('recruitment_weeks', parseInt(e.target.value))}
                className="w-full slider"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Sick days (last 6 months): {formData.sick_days}
              </label>
              <input
                type="range"
                min="0"
                max="200"
                value={formData.sick_days}
                onChange={(e) => handleInputChange('sick_days', parseInt(e.target.value))}
                className="w-full slider"
              />
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Mental health sick days (%): {formData.mental_health_percentage}%
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={formData.mental_health_percentage}
                onChange={(e) => handleInputChange('mental_health_percentage', parseInt(e.target.value))}
                className="w-full slider"
              />
              <p className="text-xs text-slate-500 mt-1">Industry average: 28%</p>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.has_presenteeism}
                  onChange={(e) => handleInputChange('has_presenteeism', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">
                  Employees work while unwell (presenteeism)
                </span>
              </label>
            </div>
          </div>

          <button
            onClick={() => setCurrentStep(1)}
            disabled={!formData.industry}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Calculate Investment Opportunity
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Loading
  if (currentStep === 1) {
    if (!loading && !results) {
      calculateResults();
    }

    return (
      <div style={widgetStyles} className={`${embedded ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8'}`}>
        <div className={`bg-white rounded-xl shadow-lg p-8 text-center ${embedded ? '' : 'max-w-2xl mx-auto'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-6"></div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">
            Calculating your analysis...
          </h3>
          <p className="text-slate-600">
            Using industry data from ONS, CIPD, Mind, and Deloitte
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Results
  if (currentStep === 2 && results) {
    return (
      <div style={widgetStyles} className={`${embedded ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8'}`}>
        <div className={`bg-white rounded-xl shadow-lg p-8 ${embedded ? '' : 'max-w-3xl mx-auto'}`}>
          {emailSent && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-800 text-sm">✅ Analysis sent to your email</p>
            </div>
          )}

          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-slate-800 mb-2">
              Your estimated annual cost is
            </h3>
            <div className="text-4xl font-bold text-blue-600 mb-4">
              {formatCurrency(results.total_cost)}
            </div>
            <p className="text-slate-600">
              Based on industry averages and your organisation profile
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
              <h4 className="font-medium text-orange-900 mb-1">Recruitment</h4>
              <div className="text-xl font-bold text-orange-700">
                {formatCurrency(results.turnover_cost)}
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <h4 className="font-medium text-yellow-900 mb-1">Absence</h4>
              <div className="text-xl font-bold text-yellow-700">
                {formatCurrency(results.absence_cost)}
              </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <h4 className="font-medium text-purple-900 mb-1">Productivity</h4>
              <div className="text-xl font-bold text-purple-700">
                {formatCurrency(results.presenteeism_cost)}
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h4 className="font-medium text-green-900 mb-3">Investment Opportunities</h4>
            <div className="text-sm text-green-800 space-y-2">
              <p>• Mental health education workshops (prices from £175/session) could prevent multiple absences</p>
              <p>• Manager training could reduce turnover by 15%</p>
              <p>• Comprehensive wellbeing programs typically deliver 25% improvement</p>
            </div>
          </div>

          {!showEmailForm ? (
            <div className="text-center space-y-3">
              <button
                onClick={() => setShowEmailForm(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Get Detailed Analysis via Email
              </button>
              <br />
              <a 
                href="https://kalobrate.com" 
                target={embedded ? "_blank" : "_self"}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                Learn more about Kalobrate →
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              <h4 className="font-medium text-slate-800">Get your detailed analysis</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="email"
                  placeholder="your.email@company.com"
                  value={emailData.email}
                  onChange={(e) => setEmailData(prev => ({...prev, email: e.target.value}))}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="First name"
                  value={emailData.first_name}
                  onChange={(e) => setEmailData(prev => ({...prev, first_name: e.target.value}))}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={sendEmail}
                  disabled={loading || !emailData.email || !emailData.first_name}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {loading ? 'Sending...' : 'Send Analysis'}
                </button>
                <button
                  onClick={() => setShowEmailForm(false)}
                  className="bg-slate-200 text-slate-800 px-4 py-2 rounded-lg hover:bg-slate-300 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export default Widget;